import {
  ConditionValue,
  DataType,
  EncContinuousDateFilterField,
  EncContinuousFilterField,
  EncDiscreteBooleanFilterField,
  EncDiscreteFilterField,
  EncSelectionCondition,
  FilterType,
  SelectionMode,
  VariableType,
} from '@mode-vegas/vegas';
import { ReportTypes } from '@mode/shared/contract-common';
import { isValid } from 'date-fns';
import {
  LegacyReportFilterControl,
  MultiValueFilterControl,
  RangeValueFilterControl,
  ReportFilterControlRecord,
  SingleValueFilterControl,
} from './report-filter-control';

export function isDiscrete(filter: ReportFilterControlRecord): boolean {
  return filter.varType === VariableType.DISCRETE;
}

export function isContinuous(filter: ReportFilterControlRecord): boolean {
  return filter.varType === VariableType.CONTINUOUS;
}

export function isDateType(filter: ReportTypes.FilterDefinition): boolean {
  switch (filter.formulaType) {
    case DataType.DATE:
    case DataType.DATETIME:
      return true;
    default:
      return false;
  }
}

export function isDiscreteDateType(filter: ReportFilterControlRecord): boolean {
  switch (filter.formulaType) {
    case DataType.DATEQTR:
    case DataType.DATEMONTH:
    case DataType.DATEWKDAY:
      return true;
    default:
      return false;
  }
}

export function isStringType(filter: ReportFilterControlRecord): boolean {
  switch (filter.formulaType) {
    case DataType.STRING:
      return true;
    default:
      return false;
  }
}

export function isNumericType(filter: ReportFilterControlRecord): boolean {
  switch (filter.formulaType) {
    case DataType.FLOAT:
    case DataType.INTEGER:
    case DataType.NUMBER:
      return true;
    default:
      return false;
  }
}

export function isDirtyFilter(filter: ReportFilterControlRecord): boolean {
  switch (filter.filterType) {
    case ReportTypes.ReportFilterType.Single:
      return filter.selected.all !== filter.default.all || filter.selected.value !== filter.default.value;
    case ReportTypes.ReportFilterType.Multi:
      return filter.selected.all !== filter.default.all || !filter.selected.values.equals(filter.default.values);
    case ReportTypes.ReportFilterType.Range:
      return filter.selected.min !== filter.default.min || filter.selected.max !== filter.default.max;
    default:
      return false;
  }
}

export function isRangeFilterOption(
  option: ReportTypes.ReportFilterOptions
): option is ReportTypes.RangeSelectFilterOptions {
  return option.filterType === ReportTypes.ReportFilterType.Range;
}

export function isSingleValueControlFilter(
  filter: ReportTypes.ReportFilterDefinition
): filter is ReportTypes.SingleValueFilterControl {
  return filter.filterType === ReportTypes.ReportFilterType.Single;
}

export function isMultiValueControlFilter(
  filter: ReportTypes.ReportFilterDefinition
): filter is ReportTypes.MultiValueFilterControl {
  return filter.filterType === ReportTypes.ReportFilterType.Multi;
}

export function isRangeValueControlFilter(
  filter: ReportTypes.ReportFilterDefinition
): filter is ReportTypes.RangeValueFilterControl {
  return filter.filterType === ReportTypes.ReportFilterType.Range;
}

export function toControl(record: ReportTypes.ReportFilterControl): LegacyReportFilterControl {
  switch (record.filterType) {
    case ReportTypes.ReportFilterType.Single:
      return SingleValueFilterControl.fromJS(record);
    case ReportTypes.ReportFilterType.Multi:
      return MultiValueFilterControl.fromJS(record);
    case ReportTypes.ReportFilterType.Range:
      return RangeValueFilterControl.fromJS(record);
  }
}

export function mergeControlType(record: ReportFilterControlRecord, controlType: ReportTypes.SelectControlType) {
  switch (record.filterType) {
    case ReportTypes.ReportFilterType.Single:
      return new SingleValueFilterControl(record).with({ controlType });
    case ReportTypes.ReportFilterType.Multi:
      return new MultiValueFilterControl(record).with({ controlType });
  }
}

export function toMultiSelectFilterField(
  filter: ReportTypes.MultiValueFilterControl | ReportTypes.SingleValueFilterControl
): EncDiscreteFilterField | EncDiscreteBooleanFilterField {
  // SingleValueSelection, delegate to toSingleSelectFilterField
  if (!('values' in filter.selected)) {
    return toSingleSelectFilterField(filter as ReportTypes.SingleValueFilterControl);
  }

  const { selected, showRelevantValuesOnly } = filter;
  const filterFieldParams = {
    formula: filter.formula,
    variable: { type: VariableType.DISCRETE },
    showRelevantValuesOnly,
    selection: {
      // TODO: Add support for SelectionMode in report filters
      mode: selected.all ? SelectionMode.ALL : SelectionMode.INCLUDE,
      selected: (selected.all ? [] : selected.values) as ConditionValue[],
    },
  };

  return filter.dataType === 'BOOLEAN'
    ? { filterType: FilterType.DiscreteBoolean, ...filterFieldParams }
    : { filterType: FilterType.Discrete, ...filterFieldParams };
}

export function toSingleSelectFilterField(
  filter: ReportTypes.SingleValueFilterControl
): EncDiscreteFilterField | EncDiscreteBooleanFilterField {
  const { selected, showRelevantValuesOnly } = filter;
  let selection = {} as EncSelectionCondition;

  if (selected.all) {
    selection = {
      mode: SelectionMode.ALL,
      selected: [],
    };
  } else {
    selection = {
      mode: SelectionMode.INCLUDE,
      selected: [selected.value ?? null],
    };
  }

  const filterFieldParams = {
    formula: filter.formula,
    variable: { type: VariableType.DISCRETE },
    showRelevantValuesOnly,
    selection,
  };

  return filter.dataType === 'BOOLEAN'
    ? { filterType: FilterType.DiscreteBoolean, ...filterFieldParams }
    : { filterType: FilterType.Discrete, ...filterFieldParams };
}

function convertSelectedValuesToDates(value: ReportTypes.ReportFilterRange) {
  const { min, max } = value;

  const minDate = min == null ? null : new Date(min);
  const maxDate = max == null ? null : new Date(max);

  if (minDate != null && isValid(minDate)) {
    minDate.setUTCHours(0, 0, 0, 0); // set to start of day
  }

  if (maxDate != null && isValid(maxDate)) {
    maxDate.setUTCHours(23, 59, 59, 999); // set last second of day
  }

  /**
   * Force casting here because vegas EncFilterField has a mistake on min/max
   * where it doesn't accept null values.
   **/

  return {
    min: (minDate && minDate.valueOf()) as number,
    max: (maxDate && maxDate.valueOf()) as number,
  };
}

export function toRangeFilterField(
  filter: ReportTypes.RangeValueFilterControl
): EncContinuousFilterField | EncContinuousDateFilterField {
  const selected = (() => {
    switch (filter.formulaType) {
      case DataType.DATETIME:
      case DataType.DATE:
        return convertSelectedValuesToDates(filter.selected);
      default: {
        return filter.selected;
      }
    }
  })();

  const filterFieldParams = {
    formula: filter.formula,
    variable: { type: VariableType.CONTINUOUS },
    includeNulls: false,
    includeMin: true,
    includeMax: true,
    range: {
      min: selected.min,
      max: selected.max,
    },
  };

  if (isDateType(filter)) {
    return {
      filterType: FilterType.ContinuousDate,
      ...filterFieldParams,
    } as EncContinuousDateFilterField; // Casting here because the vegas typings incorrectly only allows non-null range values
  } else {
    return {
      filterType: FilterType.Continuous,
      ...filterFieldParams,
    } as EncContinuousFilterField;
  }
}

export function toEncFilterField(filter: ReportTypes.ReportFilterControl) {
  switch (filter.filterType) {
    case ReportTypes.ReportFilterType.Range:
      return toRangeFilterField(filter);
    case ReportTypes.ReportFilterType.Single:
      return toSingleSelectFilterField(filter);
    case ReportTypes.ReportFilterType.Multi:
      return toMultiSelectFilterField(filter);
  }
}

export function convertToFilterControl(
  filter: ReportTypes.ReportFilterDefinition,
  selection: ReportTypes.FilterSelection,
  def: ReportTypes.FilterSelection
) {
  switch (filter.filterType) {
    case ReportTypes.ReportFilterType.Multi:
      return MultiValueFilterControl.fromJS({
        ...filter,
        filterType: filter.filterType,
        controlType: filter.controlType,
        selected: selection as any, // Control requires immutableJS objects, even though it's not needed
        default: def as any,
      });
    case ReportTypes.ReportFilterType.Single:
      return SingleValueFilterControl.fromJS({
        ...filter,
        filterType: filter.filterType,
        controlType: filter.controlType,
        selected: selection as any, // Control requires immutableJS objects, even though it's not needed
        default: def as any,
      });
    case ReportTypes.ReportFilterType.Range:
      return RangeValueFilterControl.fromJS({
        ...filter,
        filterType: filter.filterType,
        controlType: ReportTypes.ReportFilterControlType.Range,
        selected: selection as any, // Control requires immutableJS objects, even though it's not needed
        default: def as any,
      });
  }
}
